import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import { IdTypes, createFeatureScope } from 'bundles/product-features';

const { useProductFeatures } = createFeatureScope({
  enterprise: {
    enableProgramAutoJoin: false,
    enablePersonalizedReordering: false,
    enableAdminNavRedesign: false,
  },
});

/**
 * Hook to check if the Program Auto-Join feature is enabled for an organization.
 * When enabled, learners are automatically placed into programs and the removal
 * flow uses simplified "permanent removal" / "re-entry allowed" options.
 *
 * @param organizationId - The organization ID to check the feature flag for.
 * @returns Whether `enableProgramAutoJoin` is enabled for the given organization.
 */
export const useEnableProgramAutoJoin = (organizationId: string): boolean => {
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: organizationId,
    skip: !organizationId,
  });
  return features.get('enterprise', 'enableProgramAutoJoin');
};

/**
 * Hook to check if Personalized Reordering is enabled for an organization.
 *
 * @param organizationId - The organization ID to check the feature flag for.
 * @returns Whether `enablePersonalizedReordering` is enabled for the given organization.
 */
export const useEnablePersonalizedReordering = (organizationId: string): boolean => {
  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: organizationId,
    skip: !organizationId,
  });
  return features.get('enterprise', 'enablePersonalizedReordering');
};

/**
 * Returns whether the admin side nav is active.
 *
 * The admin side nav is active if the admin nav redesign feature flag is enabled
 * and the viewport is not mobile.
 *
 * @returns Whether the admin side nav is active.
 */
export const useIsAdminSideNavActive = (): boolean => {
  const isNotMobile = useMediaQuery(breakpoints.up('sm'));
  const org = useThirdPartyOrganization();

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: org?.id,
    skip: !org?.id,
  });

  const isAdminNavRedesignEnabled = features.get('enterprise', 'enableAdminNavRedesign');

  return isAdminNavRedesignEnabled && isNotMobile;
};
